import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Image from 'global/Image';
import PaywallAnalytics from 'features/paywall/analytics';
import ButtonOffering from './Button';
import {
  PAYWALL_IMAGE_URL,
  OfferingPro,
  OfferingProText,
} from '../../constants';

import {
  Modal,
  OfferingContainer,
  TextContainer,
  TitleText,
  UnderlinedText,
} from '../styled';

interface OfferingSubscriptionProps {
  defaultVisible?: boolean;
  watchParams?: boolean;
  onVisibleChange?: (visible: boolean) => void;
}

interface QueryParams {
  offeringProVisible: string;
  [key: string]: string | undefined;
}

function OfferingSubscription({
  defaultVisible = false,
  watchParams = false,
  onVisibleChange,
}: OfferingSubscriptionProps) {
  const router = useRouter();
  const { query } = router;
  const [visible, setVisible] = useState<boolean>(false);

  const handleRemoveQuery = () => {
    // Remove offeringProVisible from query
    const restQuery = { ...query } as QueryParams;
    delete restQuery.offeringProVisible;
    router.replace({ query: restQuery }, undefined, { shallow: true });
  };

  const handleVisibleChange = (payload: boolean) => {
    setVisible(payload);

    if (onVisibleChange) {
      onVisibleChange(payload);
    }
  };

  const handleNavigate = (toUrl: string) => {
    handleVisibleChange(false);

    router.push(toUrl);
  };

  useEffect(() => {
    setVisible(defaultVisible);
  }, [defaultVisible]);

  useEffect(() => {
    // Check if query has offeringProVisible
    const hasQueryVisible = query?.offeringProVisible === 'true';
    const fromDeepLink = window?.history?.length <= 2;

    if (hasQueryVisible && fromDeepLink && watchParams) {
      setVisible(true);
      handleRemoveQuery();
    }
  }, [query?.offeringProVisible]);

  return (
    <Modal
      centered
      visible={visible}
      onCancel={() => handleVisibleChange(false)}
      maskClosable={false}
      width={802}
      transitionName=""
      maskTransitionName=""
      destroyOnClose
      footer={[
        <ButtonOffering
          title={OfferingPro.open.title}
          description={OfferingPro.open.description}
          iconUrl={OfferingPro.open.iconUrl}
          onClick={() => {
            PaywallAnalytics.onClickOpenSecurities();
            handleNavigate('/securities/auth');
          }}
          isActive
        />,
        <ButtonOffering
          title={OfferingPro.subscribe.title}
          description={OfferingPro.subscribe.description}
          iconUrl={OfferingPro.subscribe.iconUrl}
          onClick={() => {
            PaywallAnalytics.onClickOpenSubscribe();
            handleNavigate('/shop');
          }}
        />,
      ]}
      title={(
        <Image
          src={`${PAYWALL_IMAGE_URL}/offering-illustration.svg`}
          id="header-illustration"
        />
      )}
    >
      <OfferingContainer>
        <TextContainer>
          <TitleText>{OfferingProText.headline}</TitleText>
          <TitleText>
            <UnderlinedText>{OfferingProText.underlined}</UnderlinedText>{' '}
            {OfferingProText.unlimited}
          </TitleText>
        </TextContainer>
      </OfferingContainer>
    </Modal>
  );
}

export default OfferingSubscription;
